// src/layouts/DashboardLayout.js
import React, { useState, useRef, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { getUser, logout as authLogout } from "../services/auth";
import { canAccess } from "../services/rbac";
import Breadcrumbs from "../components/Breadcrumbs";

import {
  FaTachometerAlt,
  FaUsers,
  FaMoneyBillWave,
  FaSms,
  FaCogs,
  FaChartBar,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaChurch,
} from "react-icons/fa";

import "./DashboardLayout.css";

export default function DashboardLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);

  const profileRef = useRef(null);
  const navigate = useNavigate();
  const user = getUser();

  /* ======================
     EFFECTS
  ====================== */
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  useEffect(() => {
    if (collapsed) setOpenGroup(null);
  }, [collapsed]);

  /* ======================
     HANDLERS
  ====================== */
  const toggleGroup = (group) => {
    if (collapsed) return;
    setOpenGroup((prev) => (prev === group ? null : group));
  };

  const handleLogout = () => {
    authLogout();
    navigate("/");
    window.location.reload();
  };

  /* ======================
     RENDER
  ====================== */
  return (
    <div className="layout-root">
      {/* SIDEBAR */}
      <aside className={`sidebar ${collapsed ? "collapsed" : ""}`}>
        {/* BRAND */}
        <div
          className="sidebar-brand clickable"
          onClick={() => setCollapsed(!collapsed)}
        >
          <FaChurch className="brand-icon" />
          {!collapsed && <span className="brand-text">ZaKa-Pay</span>}
        </div>

        {/* NAVIGATION */}
        <nav className="sidebar-nav">
          {/* DASHBOARD */}
          <NavLink to="/dashboard" className="nav-item">
            <FaTachometerAlt className="nav-icon" />
            {!collapsed && <span className="nav-label">Dashboard</span>}
          </NavLink>

          {/* MEMBERS */}
          <div className="nav-group" onClick={() => toggleGroup("members")}>
            <FaUsers className="nav-icon" />
            {!collapsed && (
              <>
                <span className="nav-label">Members</span>
                <FaChevronDown
                  className={`chevron ${openGroup === "members" ? "open" : ""}`}
                />
              </>
            )}
          </div>

          {openGroup === "members" && !collapsed && (
            <div className="nav-submenu">
              <NavLink to="/members" className="nav-subitem">
                Members
              </NavLink>
            </div>
          )}

          {/* TRANSACTIONS */}
          <div
            className="nav-group"
            onClick={() => toggleGroup("transactions")}
          >
            <FaMoneyBillWave className="nav-icon" />
            {!collapsed && (
              <>
                <span className="nav-label">Transactions</span>
                <FaChevronDown
                  className={`chevron ${
                    openGroup === "transactions" ? "open" : ""
                  }`}
                />
              </>
            )}
          </div>

          {openGroup === "transactions" && !collapsed && (
            <div className="nav-submenu">
              <NavLink to="/transactions" className="nav-subitem">
                Transactions List
              </NavLink>
            </div>
          )}

          {/* SMS */}
          <div className="nav-group" onClick={() => toggleGroup("sms")}>
            <FaSms className="nav-icon" />
            {!collapsed && (
              <>
                <span className="nav-label">SMS Management</span>
                <FaChevronDown
                  className={`chevron ${openGroup === "sms" ? "open" : ""}`}
                />
              </>
            )}
          </div>

          {openGroup === "sms" && !collapsed && (
            <div className="nav-submenu">
              <NavLink to="/sms/templates" className="nav-subitem">
                Templates
              </NavLink>
              <NavLink to="/sms/send" className="nav-subitem">
                Send SMS
              </NavLink>
              <NavLink to="/sms/logs" className="nav-subitem">
                SMS Logs
              </NavLink>
            </div>
          )}

          {/* SYSTEM SETUP */}
          {canAccess("SYSTEM_SETUP") && (
            <>
              <div className="nav-group" onClick={() => toggleGroup("setup")}>
                <FaCogs className="nav-icon" />
                {!collapsed && (
                  <>
                    <span className="nav-label">System Setup</span>
                    <FaChevronDown
                      className={`chevron ${openGroup === "setup" ? "open" : ""}`}
                    />
                  </>
                )}
              </div>

              {openGroup === "setup" && !collapsed && (
                <div className="nav-submenu">
                  {canAccess("JIMBO") && (
                    <NavLink to="/setup/jimbo" className="nav-subitem">
                      Jimbo
                    </NavLink>
                  )}
                  {canAccess("PAROKIA") && (
                    <NavLink to="/setup/parokia" className="nav-subitem">
                      Parokia
                    </NavLink>
                  )}
                  {canAccess("KANDA") && (
                    <NavLink to="/setup/kanda" className="nav-subitem">
                      Kanda
                    </NavLink>
                  )}
                  {canAccess("JUMUIA") && (
                    <NavLink to="/setup/jumuia" className="nav-subitem">
                      Jumuiya
                    </NavLink>
                  )}

                  {/* USERS (ONLY PAROKIA ADMIN) */}
                  {user?.role === "PAROKIA_ADMIN" && (
                    <NavLink to="/setup/users" className="nav-subitem">
                      Users
                    </NavLink>
                  )}
                </div>
              )}
            </>
          )}

          {/* REPORTS */}
          <div
            className="nav-group clickable"
            onClick={() => {
              toggleGroup("reports");  // expand/collapse submenu
              navigate("/reports");     // go to ReportsHome
            }}
          >
            <FaChartBar className="nav-icon" />
            {!collapsed && (
              <>
                <span className="nav-label">Reports</span>
                <FaChevronDown
                  className={`chevron ${openGroup === "reports" ? "open" : ""}`}
                />
              </>
            )}
          </div>

          {openGroup === "reports" && !collapsed && (
            <div className="nav-submenu">
              <NavLink to="/reports" className="nav-subitem">
                Reports Home
              </NavLink>

              <NavLink to="/reports/dashboard" className="nav-subitem">
                Analytics Dashboard
              </NavLink>

              <NavLink to="/reports/transactions" className="nav-subitem">
                Transaction Report
              </NavLink>

              <NavLink to="/reports/members" className="nav-subitem">
                Member Register Report
              </NavLink>

              <NavLink
                to="/reports/contribution-summary"
                className="nav-subitem"
              >
                Contribution Summary
              </NavLink>

              <NavLink to="/reports/channel-summary" className="nav-subitem">
                Channel Summary
              </NavLink>

              <NavLink to="/reports/cashier-report" className="nav-subitem">
                Cashier Report
              </NavLink>
            </div>
          )}
        </nav>

        {/* COLLAPSE BUTTON */}
        <div
          className="sidebar-collapse"
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <div className="main-area">
        <header className="top-header">
          <strong style={{ textTransform: "uppercase" }}>{user ? user.parokiaName : ""}</strong>
          <div />
          <div className="profile-area" ref={profileRef}>
            <span className="profile-text">
              Hi, <strong>{user?.username}</strong>
            </span>

            <button
              className="profile-avatar"
              onClick={() => setProfileOpen(!profileOpen)}
            >
              {user?.username?.[0]?.toUpperCase() || "U"}
            </button>

            {profileOpen && (
              <div className="profile-menu">
                <button onClick={handleLogout} className="logout-btn">
                  Logout
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="content">
          <Breadcrumbs />
          <Outlet />
        </main>
      </div>
    </div>
  );
}